import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { ArchiveReasonsResponseSchema, type ArchiveReasonsResponse } from '@shared/schemas/terra-signals';

/**
 * Archive-reasons query key root, shared here so a future invalidator (none
 * needed today — this is code-owned Salesforce picklist config, not a
 * mutable resource) has a stable constant to import.
 */
export const ARCHIVE_REASONS_KEY = ['flow-signals-archive-reasons'] as const;

/**
 * `GET /api/flow/signals/archive-reasons` (bead .15, U6). Cached
 * indefinitely (CLAUDE.md caching rule) — the manual, non-Gong-passthrough
 * archive reasons are Salesforce admin config that changes rarely and only
 * via a deploy, not a rep action, so there is nothing for a mutation to
 * invalidate.
 *
 * Plan `2026-09-25-003-pr1683-premerge-fixes-plan.md`, U4a: one `fetchJson`
 * call (`lib/apiFetchJson.ts`, U3) replaces the old hand-rolled `!res.ok` +
 * double-parse (a `safeParse` immediately followed by `validateResponse` on
 * the same raw body) — `fetchJson`'s own `schema.safeParse` is fail-closed,
 * so there is nothing left to re-check.
 */
export function useArchiveReasons() {
  const { apiFetch } = useApi();
  return useQuery<ArchiveReasonsResponse>({
    queryKey: ARCHIVE_REASONS_KEY,
    queryFn: () => fetchJson(apiFetch, ArchiveReasonsResponseSchema, '/api/flow/signals/archive-reasons'),
    staleTime: Infinity,
    // Double-error-presentation fix: a failure here already renders inline
    // (`SignalActionArchiveForm`'s `reasonsQuery.isError` → `SelectField`
    // `errorMessage`), so it must opt out of the global QueryCache toast too.
    meta: { suppressErrorToast: true },
  });
}
